import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../components/Icon';
import PromoConfirmModal from '../components/PromoConfirmModal';
import SortMenu from '../components/SortMenu';
import PageSizeDropdown from '../components/PageSizeDropdown';
import CustomDatePicker from '../components/CustomDatePicker';
import '../css/admin.css';
import '../css/pagination.css';
import {
  getPromoCodeById,
  getPromoCodeStats,
  getPromoCodeActiveUsers,
  updatePromoCodeActive,
  updatePromoCodeStartsAt,
  updatePromoCodeExpiresAt,
} from '../api';
import {
  ACTIVE_USERS_PAGE_SIZE,
  formatDisplayDate,
  normalizePromoUser,
} from '../utils/promoCodeUtils';
import { pushDetailNavState } from '../utils/detailNavState';

const AdminPromoCodeDetails = () => {
  const { promoId } = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'ru';

  const [promo, setPromo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editStartsAt, setEditStartsAt] = useState('');
  const [editExpiresAt, setEditExpiresAt] = useState('');
  const [patchLoading, setPatchLoading] = useState(null);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);

  const [activeUsers, setActiveUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [usersPage, setUsersPage] = useState(0);
  const [usersPageSize, setUsersPageSize] = useState(ACTIVE_USERS_PAGE_SIZE);
  const [usersSort, setUsersSort] = useState({ field: 'ordersCount', direction: 'DESC' });
  const [usersPagination, setUsersPagination] = useState({
    totalPages: 0,
    totalElements: 0,
  });
  const topUsersPagerRef = useRef(null);
  const shouldScrollToUsersPagerRef = useRef(false);

  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmError, setConfirmError] = useState(null);

  const loadPromo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPromoCodeById(promoId);
      setPromo(data);
      setEditStartsAt(data?.startsAt || '');
      setEditExpiresAt(data?.expiresAt || '');
    } catch (e) {
      setPromo(null);
      setError(e?.message || t('admin.promoCodes.loadError'));
    } finally {
      setLoading(false);
    }
  }, [promoId, t]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const data = await getPromoCodeStats(promoId);
      setStats(data);
    } catch (e) {
      setStats(null);
      setStatsError(e?.message || t('admin.promoCodes.statsLoadError'));
    } finally {
      setStatsLoading(false);
    }
  }, [promoId, t]);

  const loadActiveUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const data = await getPromoCodeActiveUsers(promoId, usersPage, usersPageSize, [
        { field: usersSort.field, direction: usersSort.direction },
      ]);
      const rows = (data?.content || []).map(normalizePromoUser).filter(Boolean);
      setActiveUsers(rows);
      setUsersPagination({
        totalPages: data?.totalPages ?? 0,
        totalElements: data?.totalElements ?? rows.length,
      });
      setUsersPage(data?.number ?? usersPage);
    } catch (e) {
      setActiveUsers([]);
      setUsersPagination({ totalPages: 0, totalElements: 0 });
      setUsersError(e?.message || t('admin.promoCodes.statsLoadError'));
    } finally {
      setUsersLoading(false);
    }
  }, [promoId, usersPage, usersPageSize, usersSort, t]);

  useEffect(() => {
    if (!promoId) return;
    loadPromo();
    loadStats();
  }, [promoId, loadPromo, loadStats]);

  useEffect(() => {
    setUsersPage(0);
    setUsersSort({ field: 'ordersCount', direction: 'DESC' });
    setUsersPageSize(ACTIVE_USERS_PAGE_SIZE);
  }, [promoId]);

  useEffect(() => {
    if (!promoId) return;
    loadActiveUsers();
  }, [promoId, loadActiveUsers]);

  useEffect(() => {
    if (!shouldScrollToUsersPagerRef.current) return;
    if (usersLoading) return;

    const el = topUsersPagerRef.current;
    if (!el) return;

    shouldScrollToUsersPagerRef.current = false;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [usersPage, usersLoading]);

  const activeUsersSortOptions = useMemo(
    () => [
      { value: 'fullName', label: t('admin.promoCodes.fullName') },
      { value: 'role', label: t('admin.promoCodes.role') },
      { value: 'ordersCount', label: t('admin.promoCodes.ordersCount') },
    ],
    [t]
  );

  const handleUsersSortChange = (field) => {
    setUsersSort((prev) => ({ ...prev, field }));
    setUsersPage(0);
  };

  const handleUsersDirectionChange = (direction) => {
    setUsersSort((prev) => ({ ...prev, direction }));
    setUsersPage(0);
  };

  const handleUsersPageChange = (page, scrollFromBottom = false) => {
    if (scrollFromBottom) {
      shouldScrollToUsersPagerRef.current = true;
    }
    setUsersPage(page);
  };

  const handleUsersPageSizeChange = (newSize) => {
    setUsersPageSize(parseInt(newSize, 10));
    shouldScrollToUsersPagerRef.current = true;
    setUsersPage(0);
  };

  const getUsersVisiblePages = () => {
    const totalPages = usersPagination.totalPages || 0;
    const current = usersPage;
    if (totalPages <= 1) return [];
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);

    const pages = [0];
    const left = Math.max(1, current - 1);
    const right = Math.min(totalPages - 2, current + 1);
    if (left > 1) pages.push('left-ellipsis');
    for (let p = left; p <= right; p += 1) pages.push(p);
    if (right < totalPages - 2) pages.push('right-ellipsis');
    pages.push(totalPages - 1);
    return pages;
  };

  const renderActiveUsersPager = (position = 'bottom') => {
    if ((usersLoading && activeUsers.length === 0) || usersPagination.totalPages <= 0) {
      return null;
    }

    const isFirst = usersPage <= 0;
    const isLast = usersPage >= usersPagination.totalPages - 1;
    const pages = getUsersVisiblePages();
    const total = usersPagination.totalElements || 0;
    const from = total > 0 ? usersPage * usersPageSize + 1 : 0;
    const to = Math.min((usersPage + 1) * usersPageSize, total);

    return (
      <div
        className={`orders-pager orders-pager-${position}`}
        ref={position === 'top' ? topUsersPagerRef : null}
      >
        <div className="orders-pager-info">
          {t('common.pagination.showing', { from, to, total })}
        </div>

        <div className="orders-pager-controls">
          <button
            type="button"
            className="orders-pager-nav"
            onClick={() => handleUsersPageChange(usersPage - 1, position === 'bottom')}
            disabled={isFirst || usersLoading}
          >
            {t('common.pagination.previous')}
          </button>

          <div className="orders-pager-pages">
            {pages.map((item) => {
              if (typeof item !== 'number') {
                return <span key={item} className="orders-pager-ellipsis">...</span>;
              }
              const active = item === usersPage;
              return (
                <button
                  key={item}
                  type="button"
                  className={`orders-pager-page ${active ? 'active' : ''}`}
                  onClick={() => handleUsersPageChange(item, position === 'bottom')}
                  disabled={usersLoading}
                  aria-current={active ? 'page' : undefined}
                >
                  {item + 1}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className="orders-pager-nav"
            onClick={() => handleUsersPageChange(usersPage + 1, position === 'bottom')}
            disabled={isLast || usersLoading}
          >
            {t('common.pagination.next')}
          </button>

          <PageSizeDropdown
            value={usersPageSize}
            onChange={handleUsersPageSizeChange}
            options={[10, 20, 50, 100]}
            position={position}
          />
        </div>
      </div>
    );
  };

  const applyPatch = async (key, fn, { forConfirm = false } = {}) => {
    if (!promo?.id) return false;
    setPatchLoading(key);
    setError(null);
    if (forConfirm) setConfirmError(null);
    try {
      const updated = await fn();
      setPromo(updated);
      if (updated.startsAt != null) setEditStartsAt(updated.startsAt);
      if (updated.expiresAt != null) setEditExpiresAt(updated.expiresAt);
      loadStats();
      loadActiveUsers();
      return true;
    } catch (e) {
      const message = e?.message || t('admin.promoCodes.updateError');
      if (forConfirm) setConfirmError(message);
      else setError(message);
      return false;
    } finally {
      setPatchLoading(null);
    }
  };

  const closeConfirm = () => {
    if (patchLoading) return;
    setConfirmAction(null);
    setConfirmError(null);
  };

  const handleToggleActive = () => {
    if (!promo) return;
    setConfirmError(null);
    setConfirmAction({ type: 'active', nextActive: !promo.active });
  };

  const handleSaveStartsAt = () => {
    const startsAt = editStartsAt || null;
    if (!startsAt) {
      setError(t('admin.promoCodes.errorDate'));
      return;
    }
    setError(null);
    setConfirmError(null);
    setConfirmAction({ type: 'startsAt', value: startsAt });
  };

  const handleSaveExpiresAt = () => {
    const expiresAt = editExpiresAt || null;
    if (!expiresAt) {
      setError(t('admin.promoCodes.errorDate'));
      return;
    }
    setError(null);
    setConfirmError(null);
    setConfirmAction({ type: 'expiresAt', value: expiresAt });
  };

  const confirmModalConfig = useMemo(() => {
    if (!confirmAction || !promo) return null;

    const baseItems = [{ label: t('admin.promoCodes.code'), value: promo.code }];

    if (confirmAction.type === 'active') {
      return {
        title: confirmAction.nextActive
          ? t('admin.promoCodes.confirm.activateTitle')
          : t('admin.promoCodes.confirm.deactivateTitle'),
        subtitle: t('admin.promoCodes.confirm.updateSubtitle'),
        items: [
          ...baseItems,
          {
            label: t('admin.promoCodes.active'),
            value: confirmAction.nextActive
              ? t('admin.promoCodes.activeYes')
              : t('admin.promoCodes.activeNo'),
          },
        ],
      };
    }

    if (confirmAction.type === 'startsAt') {
      return {
        title: t('admin.promoCodes.confirm.updateStartsAtTitle'),
        subtitle: t('admin.promoCodes.confirm.updateSubtitle'),
        items: [
          ...baseItems,
          {
            label: t('admin.promoCodes.startsAt'),
            value: formatDisplayDate(confirmAction.value, locale),
          },
        ],
      };
    }

    if (confirmAction.type === 'expiresAt') {
      return {
        title: t('admin.promoCodes.confirm.updateExpiresAtTitle'),
        subtitle: t('admin.promoCodes.confirm.updateSubtitle'),
        items: [
          ...baseItems,
          {
            label: t('admin.promoCodes.expiresAt'),
            value: formatDisplayDate(confirmAction.value, locale),
          },
        ],
      };
    }

    return null;
  }, [confirmAction, promo, locale, t]);

  const handleConfirmAction = async () => {
    if (!promo || !confirmAction) return;

    let ok = false;
    if (confirmAction.type === 'active') {
      ok = await applyPatch(
        'active',
        () => updatePromoCodeActive(promo.id, confirmAction.nextActive),
        { forConfirm: true }
      );
    } else if (confirmAction.type === 'startsAt') {
      ok = await applyPatch(
        'startsAt',
        () => updatePromoCodeStartsAt(promo.id, confirmAction.value),
        { forConfirm: true }
      );
    } else if (confirmAction.type === 'expiresAt') {
      ok = await applyPatch(
        'expiresAt',
        () => updatePromoCodeExpiresAt(promo.id, confirmAction.value),
        { forConfirm: true }
      );
    }

    if (ok) {
      setConfirmAction(null);
      setConfirmError(null);
    }
  };

  return (
    <div className="page-content admin-page">
    <div
      className="admin-promo-page admin-promo-detail-page"
    >

      {loading && (
        <div className="admin-promo-page-card">
          <p className="admin-promo-muted">{t('common.loading')}</p>
        </div>
      )}

      {!loading && error && !promo && (
        <div className="admin-promo-page-card" role="alert">
          <div className="form-status error">{error}</div>
        </div>
      )}

      {!loading && promo && (
        <>
          <header className="admin-promo-detail-hero">
            <div className="admin-promo-detail-hero-main">
              <div className="admin-promo-detail-hero-icon">
                <Icon name="tag" size={22} />
              </div>
              <div>
                <h1 className="admin-promo-detail-hero-code">
                  <code>{promo.code}</code>
                </h1>
                <p className="admin-promo-detail-hero-meta">
                  {t(`admin.promoCodes.types.${promo.type}`, promo.type)}
                  <span className="admin-promo-detail-hero-dot">·</span>
                  {t('admin.promoCodes.createdAt')}: {formatDisplayDate(promo.createdAt, locale)}
                </p>
              </div>
            </div>
            <div className="admin-promo-hero-status">
              <span className={`admin-promo-badge large ${promo.active ? 'active' : 'inactive'}`}>
                {promo.active ? t('admin.promoCodes.activeYes') : t('admin.promoCodes.activeNo')}
              </span>
              <button
                type="button"
                className="admin-promo-hero-status-edit"
                onClick={handleToggleActive}
                disabled={patchLoading === 'active'}
                aria-label={t('admin.promoCodes.editStatus')}
                title={t('admin.promoCodes.editStatus')}
              >
                <Icon name="pencil" size={16} />
              </button>
            </div>
          </header>

          {error && (
            <div className="form-status error admin-promo-detail-alert" role="alert">
              {error}
            </div>
          )}

          <div className="admin-promo-detail-layout">
            <section className="admin-promo-page-card admin-promo-settings-card">
              <h2 className="admin-promo-card-heading">{t('admin.promoCodes.settingsTitle')}</h2>

              <div className="admin-promo-detail-row admin-promo-detail-row--edit">
                <label className="admin-promo-detail-label">
                  {t('admin.promoCodes.startsAt')}
                </label>
                <div className="admin-promo-edit-row">
                  <CustomDatePicker
                    selected={editStartsAt ? new Date(editStartsAt) : null}
                    onChange={(date) => setEditStartsAt(date ? date.toISOString() : '')}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary admin-promo-save-btn"
                    onClick={handleSaveStartsAt}
                    disabled={patchLoading === 'startsAt'}
                  >
                    {patchLoading === 'startsAt' ? t('admin.promoCodes.saving') : t('common.save')}
                  </button>
                </div>
              </div>

              <div className="admin-promo-detail-row admin-promo-detail-row--edit">
                <label className="admin-promo-detail-label">
                  {t('admin.promoCodes.expiresAt')}
                </label>
                <div className="admin-promo-edit-row">
                  <CustomDatePicker
                    selected={editExpiresAt ? new Date(editExpiresAt) : null}
                    onChange={(date) => setEditExpiresAt(date ? date.toISOString() : '')}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary admin-promo-save-btn"
                    onClick={handleSaveExpiresAt}
                    disabled={patchLoading === 'expiresAt'}
                  >
                    {patchLoading === 'expiresAt' ? t('admin.promoCodes.saving') : t('common.save')}
                  </button>
                </div>
              </div>
            </section>

            <section className="admin-promo-page-card admin-promo-stats-card">
              <h2 className="admin-promo-card-heading">{t('admin.promoCodes.statsTitle')}</h2>
              {statsLoading && <p className="admin-promo-muted">{t('common.loading')}</p>}
              {statsError && !statsLoading && (
                <div className="form-status error" role="alert">{statsError}</div>
              )}
              {!statsLoading && stats && (
                <div className="admin-promo-stats-grid">
                  <div className="admin-promo-stat-card">
                    <span className="admin-promo-stat-label">{t('admin.promoCodes.totalUsed')}</span>
                    <span className="admin-promo-stat-value">
                      {Number(stats.totalUsed ?? 0).toLocaleString(locale)}
                    </span>
                  </div>
                  <div className="admin-promo-stat-card">
                    <span className="admin-promo-stat-label">{t('admin.promoCodes.activeUsersStat')}</span>
                    <span className="admin-promo-stat-value">
                      {Number(stats.activeUsers ?? 0).toLocaleString(locale)}
                    </span>
                  </div>
                </div>
              )}
            </section>
          </div>

          <section className="admin-promo-page-card admin-promo-users-card">
            <div className="admin-promo-users-header">
              <h2 className="admin-promo-card-heading">{t('admin.promoCodes.activeUsersTitle')}</h2>
              <SortMenu
                sort={usersSort.field}
                direction={usersSort.direction}
                onSortChange={handleUsersSortChange}
                onDirectionChange={handleUsersDirectionChange}
                sortOptions={activeUsersSortOptions}
                translationKey="orders"
              />
            </div>
            {usersLoading && activeUsers.length === 0 && (
              <p className="admin-promo-muted">{t('common.loading')}</p>
            )}
            {usersError && !usersLoading && (
              <div className="form-status error" role="alert">{usersError}</div>
            )}
            {!usersError && activeUsers.length === 0 && !usersLoading && (
              <p className="admin-promo-muted">{t('admin.promoCodes.activeUsersEmpty')}</p>
            )}
            {!usersError && (activeUsers.length > 0 || usersPagination.totalElements > 0) && (
              <>
                {renderActiveUsersPager('top')}
                <div className="admin-promo-table-wrap">
                  <table className="admin-promo-table admin-promo-users-table">
                    <thead>
                      <tr>
                        <th>{t('admin.promoCodes.fullName')}</th>
                        <th>{t('admin.promoCodes.role')}</th>
                        <th>{t('admin.promoCodes.ordersCount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeUsers.map((user) => (
                        <tr key={user.userId}>
                          <td>
                            {user.userId ? (
                              <Link
                                to={`/users/${user.userId}`}
                                state={pushDetailNavState(location, { fromPromo: location.pathname })}
                                className="admin-promo-user-link"
                              >
                                {user.fullName}
                              </Link>
                            ) : (
                              user.fullName
                            )}
                          </td>
                          <td>{t(`users.roles.${user.role}`, user.role)}</td>
                          <td>{user.ordersCount.toLocaleString(locale)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {usersLoading && activeUsers.length > 0 && (
                  <p className="admin-promo-muted">{t('common.loading')}</p>
                )}
                {renderActiveUsersPager('bottom')}
              </>
            )}
          </section>
        </>
      )}
    </div>

      <PromoConfirmModal
        open={Boolean(confirmAction && confirmModalConfig)}
        title={confirmModalConfig?.title ?? ''}
        subtitle={confirmModalConfig?.subtitle}
        items={confirmModalConfig?.items ?? []}
        error={confirmError}
        submitting={Boolean(patchLoading)}
        onCancel={closeConfirm}
        onConfirm={handleConfirmAction}
        cancelLabel={t('admin.promoCodes.confirm.cancel')}
        confirmLabel={patchLoading ? t('admin.promoCodes.saving') : t('admin.promoCodes.confirm.confirm')}
        titleId="promo-update-confirm-title"
      />
    </div>
  );
};

export default AdminPromoCodeDetails;
